"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Constants
// ============================================================================

const TEMPLATE_KEYS = [1, 2, 3, 4] as const;
type TemplateKey = (typeof TEMPLATE_KEYS)[number];

const INBOX_KEYS = [1, 2, 3] as const;
type InboxKey = (typeof INBOX_KEYS)[number];

// Each template is bound to one inbox (active during cycling)
const TEMPLATE_INBOX: Record<TemplateKey, InboxKey> = {
  1: 3, // Cold Outreach -> Cold Outreach inbox (Resend)
  2: 1, // Follow-up -> Sales Team
  3: 1, // Quote -> Sales Team
  4: 1, // Reminder -> Sales Team
};

const TOKEN_PATTERN = /(\[[a-zA-Z]+\])/g;
const ROTATION_MS = 6000;

// ============================================================================
// Root Section
// ============================================================================

export const EmailSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <EmailScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("email");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <EnvelopeIcon className="h-3.5 w-3.5" />
        Email
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Email Scene
// ============================================================================

const EmailScene = () => {
  const t = useTranslations("email");
  const [active, setActive] = React.useState<TemplateKey>(1);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const i = TEMPLATE_KEYS.indexOf(prev);
        return TEMPLATE_KEYS[(i + 1) % TEMPLATE_KEYS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  // Token map — values resolved from translations
  const tokens: Record<string, string> = {
    salutation: t("lead_salutation"),
    firstName: t("lead_first_name"),
    lastName: t("lead_last_name"),
    role: t("lead_role"),
    company: t("lead_company"),
    repFirstName: t("rep_first_name"),
    meetingTime: t("meeting_time"),
    priceMonthly: t("price_monthly"),
  };

  const activeInbox: InboxKey = TEMPLATE_INBOX[active];

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[620px]">
        <InboxRail activeInbox={activeInbox} />
        <ComposerPane
          active={active}
          onSelectTemplate={setActive}
          tokens={tokens}
        />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Inbox rail (left)
// ----------------------------------------------------------------------------

const InboxRail = ({ activeInbox }: { activeInbox: InboxKey }) => {
  const t = useTranslations("email");
  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <EnvelopeIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("inboxes_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{t("inboxes_meta")}</span>
      </div>

      <ul className="space-y-2">
        {INBOX_KEYS.map((key) => (
          <InboxCard key={key} inboxKey={key} active={key === activeInbox} />
        ))}
      </ul>

      {/* Outbound configs */}
      <div className="mt-5 pt-4 border-t border-neutral-800">
        <div className="text-[10px] font-semibold tracking-wider text-neutral-400 uppercase mb-2">
          {t("outbound_label")}
        </div>
        <div className="space-y-1.5">
          {[1, 2].map((idx) => (
            <div
              key={idx}
              className="flex items-center justify-between rounded-lg border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5"
            >
              <span className="text-[11px] font-mono text-neutral-300 truncate">
                {t(`outbound_${idx}_name`)}
              </span>
              <span className="text-[9px] uppercase tracking-wider font-semibold text-red-300 bg-red-500/10 ring-1 ring-red-500/30 rounded px-1.5 py-0.5 whitespace-nowrap flex-shrink-0">
                {t(`outbound_${idx}_provider`)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const InboxCard = ({
  inboxKey,
  active,
}: {
  inboxKey: InboxKey;
  active: boolean;
}) => {
  const t = useTranslations("email");
  const usersString = t(`inbox_${inboxKey}_users`);
  const userInitials = usersString.split(/[·•]/).map((s) => s.trim());
  return (
    <li
      className={cn(
        "rounded-xl border p-3 transition-colors",
        active
          ? "border-red-500/50 bg-neutral-900"
          : "border-neutral-800 bg-neutral-900/40"
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={cn(
            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ring-1",
            active
              ? "bg-red-500/15 text-red-300 ring-red-500/40"
              : "bg-neutral-800 text-neutral-400 ring-neutral-700"
          )}
        >
          <InboxIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "text-sm font-semibold truncate",
                active ? "text-white" : "text-neutral-300"
              )}
            >
              {t(`inbox_${inboxKey}_name`)}
            </span>
            <span
              className={cn(
                "text-[9px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 ring-1 whitespace-nowrap flex-shrink-0",
                active
                  ? "text-red-200 bg-red-500/15 ring-red-500/40"
                  : "text-neutral-500 bg-neutral-900 ring-neutral-800"
              )}
            >
              {t(`inbox_${inboxKey}_provider`)}
            </span>
          </div>
          <div className="text-[11px] text-neutral-500 truncate font-mono mt-0.5">
            {t(`inbox_${inboxKey}_address`)}
          </div>
          <div className="mt-2 flex items-center -space-x-1.5">
            {userInitials.map((u, i) => (
              <span
                key={i}
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ring-2 ring-neutral-900",
                  active
                    ? "bg-red-500/20 text-red-200"
                    : "bg-neutral-800 text-neutral-400"
                )}
              >
                {u}
              </span>
            ))}
          </div>
        </div>
      </div>
    </li>
  );
};

// ----------------------------------------------------------------------------
// Composer pane (right)
// ----------------------------------------------------------------------------

const ComposerPane = ({
  active,
  onSelectTemplate,
  tokens,
}: {
  active: TemplateKey;
  onSelectTemplate: (k: TemplateKey) => void;
  tokens: Record<string, string>;
}) => {
  const t = useTranslations("email");
  const attachment = t(`tpl_${active}_attachment`);
  const hasAttachment = attachment && attachment.length > 0;

  return (
    <div className="lg:col-span-8 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      {/* Template selector */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto">
        <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase mr-2 flex-shrink-0">
          {t("templates_label")}
        </span>
        {TEMPLATE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelectTemplate(key)}
            className={cn(
              "text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap transition-colors",
              active === key
                ? "bg-red-500/15 text-red-200 ring-1 ring-red-500/30"
                : "text-neutral-500 hover:text-neutral-300"
            )}
          >
            {t(`tpl_${key}_name`)}
          </button>
        ))}
      </div>

      {/* Composer card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="rounded-xl border border-neutral-800 bg-neutral-900 flex-1 flex flex-col overflow-hidden"
        >
          {/* Subject row */}
          <div className="border-b border-neutral-800 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">
              {t("subject_label")}
            </div>
            <div className="text-base font-semibold text-white leading-tight">
              <TokenizedText
                rawText={t(`tpl_${active}_subject`)}
                tokens={tokens}
              />
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 px-4 py-4 space-y-3 text-[15px] text-neutral-200 leading-relaxed">
            {[1, 2, 3, 4].map((line, i) => (
              <BodyLine
                key={`${active}-${line}`}
                rawText={t(`tpl_${active}_body_${line}`)}
                tokens={tokens}
                index={i}
              />
            ))}
          </div>

          {/* Composer footer */}
          <div className="border-t border-neutral-800 px-4 py-3 flex flex-wrap items-center gap-2 bg-neutral-900/60">
            <FooterChip
              label={t("outbound_field_label")}
              value={t(`tpl_${active}_outbound`)}
              icon={<EnvelopeIcon className="h-3 w-3" />}
            />
            <FooterChip
              label={t("schedule_field_label")}
              value={t(`tpl_${active}_schedule`)}
              icon={<ClockIcon className="h-3 w-3" />}
            />
            {hasAttachment && (
              <FooterChip
                label={t("attachment_field_label")}
                value={attachment}
                icon={<PaperclipIcon className="h-3 w-3" />}
              />
            )}
            <div className="flex-1" />
            <button
              type="button"
              className="text-[11px] font-semibold uppercase tracking-wider text-red-200 bg-red-500/15 ring-1 ring-red-500/40 rounded-md px-3 py-1.5 hover:bg-red-500/25 transition-colors"
            >
              {t("send_button")}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const BodyLine = ({
  rawText,
  tokens,
  index,
}: {
  rawText: string;
  tokens: Record<string, string>;
  index: number;
}) => (
  <motion.p
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, delay: 0.1 + index * 0.06 }}
  >
    <TokenizedText rawText={rawText} tokens={tokens} />
  </motion.p>
);

const TokenizedText = ({
  rawText,
  tokens,
}: {
  rawText: string;
  tokens: Record<string, string>;
}) => {
  const parts = rawText.split(TOKEN_PATTERN).filter(Boolean);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("[") && part.endsWith("]")) {
          const tokenName = part.slice(1, -1);
          const value = tokens[tokenName] ?? part;
          return (
            <span
              key={i}
              className="inline-flex items-baseline rounded px-1.5 py-0.5 mx-0.5 bg-red-500/15 text-red-200 ring-1 ring-red-500/30 font-medium"
              title={`{${tokenName}}`}
            >
              {value}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

const FooterChip = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) => (
  <span className="inline-flex items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-[11px]">
    <span className="text-neutral-500">{icon}</span>
    <span className="text-neutral-500 uppercase tracking-wider text-[9px] font-semibold">
      {label}
    </span>
    <span className="text-neutral-300 font-mono tabular-nums">{value}</span>
  </span>
);

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconInboxes /> },
  { key: "t2", icon: <IconIdentity /> },
  { key: "t3", icon: <IconTokens /> },
  { key: "t4", icon: <IconClock /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("email");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function EnvelopeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5 4h14l3 8v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="m21 11-9 9a5 5 0 1 1-7-7l9-9a3 3 0 1 1 4 4l-9 9a1 1 0 1 1-1-1l8-8" />
    </svg>
  );
}

function IconInboxes() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="6" width="18" height="4" rx="1" />
      <rect x="3" y="12" width="18" height="4" rx="1" />
      <rect x="3" y="18" width="18" height="3" rx="1" />
    </svg>
  );
}

function IconIdentity() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c.6-3 3-4.5 6-4.5s5.4 1.5 6 4.5" />
      <path d="M16 8h5M16 12h5M16 16h3" />
    </svg>
  );
}

function IconTokens() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M5 7h3l1-2h4l1 2h3M4 7h16v12H4z" />
      <path d="M9 12h6M9 15h4" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5M9 2h6" />
    </svg>
  );
}
